import { useGetInvoices } from '@utils/api/invoices/use-get-invoices.tsx';
import { useMemo, useState } from 'react';
import { formatDate } from '@utils/date.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useDownloadInvoice } from '@utils/api/invoices/use-download-invoice.tsx';
import { isAxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Download } from 'lucide-react';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useOrgCurrency } from '@utils/hooks/use-org-currency';
import { formatCurrency } from '@utils/helpers/currency';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type InvoiceRow = {
  id: number;
  invoice_ref: string;
  invoice_date: string; // formatted
  amount: string | number;
  payment_status: string;
};

export const Invoices = () => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';
  const { data: invoices, isLoading, isFetching } = useGetInvoices();
  const currency = useOrgCurrency();

  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const { mutate: downloadInvoice } = useDownloadInvoice();

  const rows = useMemo<InvoiceRow[]>(() => {
    if (!invoices) return [];
    return invoices.result.map(inv => ({
      id: inv.id,
      invoice_ref: inv.invoice_ref,
      amount: inv.total_price_excl,
      invoice_date: inv.invoice_date ? formatDate(inv.invoice_date) : '-',
      payment_status: inv.payment_status === 4 ? t('open') : t('')
    }));
  }, [invoices, t]);

  const columns = useMemo<DataGridColumn<InvoiceRow>[]>(
    () => [
      {
        id: 'ref',
        accessorKey: 'invoice_ref',
        header: t('invoice-ref'),
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'ref',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'invoice_date',
        accessorKey: 'invoice_date',
        header: t('invoice-date'),
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '-')
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: `${t('amount')} (${currency})`,
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const val = Number(getValue());
          return isNaN(val)
            ? String(getValue() ?? '')
            : formatCurrency(val, currency);
        }
      },
      {
        id: 'payment_status',
        accessorKey: 'payment_status',
        header: t('payment-status'),
        minWidth: 160,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'status',
        serverOperator: 'eq',
        filterOptions: [
          { label: 'Open', value: '4' },
          { label: 'Paid partial', value: '2' },
          { label: 'Credited', value: '8' },
          { label: 'Not started', value: '16' },
          { label: 'Failed', value: '32' }
        ],
        cell: ({ getValue }) => {
          const value = String(getValue() ?? '').trim();
          if (!value) return null;
          const isOpen = value === t('open');
          const colors = isOpen
            ? {
                bg: isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.10)',
                text: isDark ? 'rgb(251,191,36)' : 'rgb(217,119,6)',
                ring: 'rgba(245,158,11,0.25)'
              }
            : {
                bg: getColour('appSoft'),
                text: getColour('inactiveText'),
                ring: getColour('hairline')
              };
          return (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                boxShadow: `0 0 0 1px ${colors.ring}`
              }}
            >
              {value}
            </span>
          );
        }
      }
    ],
    [t, currency, isDark, getColour]
  );

  const actions = useMemo<GridAction<InvoiceRow>[]>(
    () => [
      {
        id: 'download',
        label: t('download'),
        icon: <Download className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedInvoiceId(String(row.id));
          setDownloadModalOpen(true);
        }
      }
    ],
    [t]
  );

  const handleDownloadInvoice = async (invoiceId: string) => {
    setIsDownloading(true);
    try {
      await downloadInvoice({ invoice_id: Number(invoiceId) });
      toast.success(t('invoice-downloaded-successfully'));
    } catch (error) {
      let message = t('an-error-has-occurred');
      if (isAxiosError<BaseError>(error)) {
        const data = error.response?.data;
        message =
          data?.error_description ||
          data?.detail ||
          data?.error ||
          data?.message ||
          message;
      }
      toast.error(message);
    } finally {
      setDownloadModalOpen(false);
      setIsDownloading(false);
    }
  };

  return (
    <div className="py-4">
      <div className="py-2 p-2 md:p-0 min-w-full">
        <DataGridV2<InvoiceRow>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={invoices ? invoices.total_results : undefined}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="82vh"
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !invoices}
          getRowId={row => String(row.id)}
        />
      </div>

      {selectedInvoiceId && (
        <ConfirmationModal
          isOpen={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          onConfirm={() => handleDownloadInvoice(selectedInvoiceId)}
          size="small"
          title={t('download-invoice')}
          caption={t('are-you-sure-you-want-to-download-this-invoice')}
          confirmLabel={isDownloading ? t('downloading') : t('download')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDownloading}
          destructive={false}
          autoFocusConfirm
        />
      )}
    </div>
  );
};
